import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '../../lib/utils.js';

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        authPrimary: 'bg-primary text-primary-foreground hover:bg-primary/90',
        authMuted: 'bg-muted text-foreground hover:bg-muted/80',
        tab: 'rounded-none border border-transparent border-b-2 bg-transparent text-muted-foreground shadow-none hover:bg-muted/60 hover:text-foreground aria-selected:border-b-primary aria-selected:text-foreground',
        sidebarItem:
          'h-auto w-full flex-col items-start justify-start border border-transparent bg-transparent p-3 text-left text-sm font-medium text-foreground shadow-none hover:bg-accent/50 data-[active=true]:border-border data-[active=true]:bg-accent/60',
        projectCard:
          'h-auto w-full flex-col items-start gap-1.5 rounded-md border border-input bg-card p-4 text-left text-foreground shadow-none hover:bg-accent/40',
        outlineLink:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground no-underline dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        disclosure:
          'h-auto min-h-0 rounded-none border-0 bg-transparent p-0 text-muted-foreground shadow-none hover:bg-transparent hover:text-foreground focus-visible:ring-0',
      },
      size: {
        default: 'h-10 px-4 py-2 has-[>svg]:px-3',
        xs: "h-[26px] gap-1.5 rounded-md px-2.5 text-[12px] has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3.5",
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        auth: 'h-10 rounded-lg px-3',
        'auth-lg': 'h-11 rounded-lg px-3',
        tab: 'min-h-[44px] px-3 text-[13px]',
        icon: 'size-9',
        'icon-xs': "size-[26px] rounded-md [&_svg:not([class*='size-'])]:size-3.5",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(function Button(
  { className, variant = 'default', size = 'default', asChild = false, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
});

export { Button, buttonVariants };
